import {
    type FC,
    useContext,
    useEffect,
    useMemo,
    useRef,
} from "react";
import { useDragLayer } from "react-dnd";
import { createPortal } from "react-dom";
import FontAwesomeLight from "../../../Utilities/Platform/FontAwesome/FontAwesomeLight";
import { DragDropEditorContext, type DragDropItem } from "./DragDropTypes";

export const DragDropDragLayer: FC = () => {
    const { items } = useContext(DragDropEditorContext);

    const dragLayerRef = useRef<HTMLDivElement | null>(null);

    useEffect(() => {
        dragLayerRef.current = document.getElementById("drag-layer") as HTMLDivElement;
    }, []);

    const {
        item,
        currentOffset,
        isDragging,
    } = useDragLayer(monitor => ({
        item         : monitor.getItem<DragDropItem | null>(),
        currentOffset: monitor.getSourceClientOffset(),
        isDragging   : monitor.isDragging(),
    }));

    useEffect(() => {
        if(isDragging) {
            document.body.classList.add("select-none");
        }

        return () => {
            document.body.classList.remove("select-none");
        };
    }, [ isDragging ]);

    const definition = useMemo(() => {
        if(!item) {
            return null;
        }

        if(!item.type) {
            return null;
        }

        if(item.type in items) {
            return items[item.type as keyof typeof items];
        }

        return null;
    }, [ item, items ]);

    if(!definition || !currentOffset || !isDragging || !dragLayerRef.current) {
        return null;
    }

    return createPortal(
        <div className="fixed pointer-events-none z-50 left-0 top-0 w-full h-full overflow-hidden">
            <div
                className={
                    "flex items-center justify-center flex-col w-24 h-24 p-1 rounded shadow " +
                    "text-center border border-neutral-300 bg-neutral-50 dark:border-0 dark:bg-dark-900 "
                }
                style={{
                    transform: `translate(${currentOffset.x}px, ${currentOffset.y}px)`,
                }}
            >
                <div className="text-3xl mb-1">
                    <FontAwesomeLight icon={definition.icon} />
                </div>
                {definition.name}
            </div>
        </div>,
        dragLayerRef.current
    );
};
